import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { fetchMessages, sendMessage, markMessagesRead, groupThreads } from '../../lib/messages';
import { s } from '../../lib/workerStyles';
import { useVisiblePolling } from '../../lib/useVisiblePolling';

const MESSAGE_MAX_LEN = 2000;

// Local light-theme overrides layered on top of the shared `s` styles —
// `lib/workerStyles` is shared with the worker-facing app, which isn't part
// of this redesign, so it stays untouched and this file overrides on top
// instead of editing it directly.
const m = {
  screen: { background: '#F5F3EF' },
  header: { borderBottom: '1px solid #E4E0D8' },
  title: { color: '#1A2B33' },
  sub: { color: '#5B6B72' },
  empty: { color: '#5B6B72' },
  threadHead: { color: '#1A2B33' },
  threadRow: { background: '#FFFFFF', border: '1px solid #E4E0D8' },
  threadRowName: { color: '#1A2B33' },
  threadRowPreview: { color: '#5B6B72' },
  badge: { color: '#FFFFFF' },
  linkBtn: { color: '#1F3A47' },
  composeLabel: { color: '#5B6B72' },
  select: { background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#1A2B33' },
  textarea: { background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#1A2B33' },
  sendBtn: { color: '#FFFFFF' },
};

// The DB stores roles as 'worker'/'supervisor'/'manager'; those are internal
// names, not what Fabian calls his staff.
const ROLE_LABELS = { worker: 'Cleaner', supervisor: 'Supervisor', manager: 'Manager' };

export default function ManagerMessages() {
  const { profile } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [body, setBody] = useState('');
  const [workers, setWorkers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [composing, setComposing] = useState(false);
  const [newRecipientId, setNewRecipientId] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const msgs = await fetchMessages();
      const grouped = groupThreads(msgs, profile.id);
      setThreads(grouped);
      return grouped;
    } catch (e) {
      // Plain-language banner for the manager; real reason stays in the
      // console so it's still diagnosable.
      console.error('Failed to load messages:', e);
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [profile.id]);

  useVisiblePolling(load, 30000);

  useEffect(() => {
    supabase
      .from('profiles')
      .select('id, full_name, role')
      .in('role', ['worker', 'supervisor', 'manager'])
      .neq('id', profile.id)
      .order('full_name')
      .then(({ data }) => {
        setWorkers(data || []);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id]);

  async function openThread(t) {
    setActiveThread(t);
    const unreadIds = t.messages.filter(m => !m.read && m.recipient_id === profile.id).map(m => m.id);
    if (unreadIds.length > 0) {
      try { await markMessagesRead(unreadIds); } catch (_) {}
    }
    await load();
  }

  async function handleSend() {
    if (!activeThread || !body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await sendMessage({
        senderId: profile.id,
        senderRole: profile.role,
        recipientId: activeThread.otherId,
        body,
      });
      setBody('');
      const refreshedThreads = await load();
      const refreshed = refreshedThreads?.find(x => x.otherId === activeThread.otherId);
      if (refreshed) setActiveThread(refreshed);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  }

  async function handleStartNewMessage() {
    if (!newRecipientId || !body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await sendMessage({
        senderId: profile.id,
        senderRole: profile.role,
        recipientId: newRecipientId,
        body,
      });
      setBody('');
      setComposing(false);
      const refreshedThreads = await load();
      const started = refreshedThreads?.find(x => x.otherId === newRecipientId);
      setActiveThread(started || { otherId: newRecipientId, messages: [], unread: 0 });
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  }

  function nameOf(id) {
    const w = workers.find(x => x.id === id);
    return w ? w.full_name : 'Unknown';
  }
  function fmt(ts) {
    return new Date(ts).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  return (
    <div style={{ ...s.screen, ...m.screen }}>
      <div style={{ ...s.header, ...m.header }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ ...s.title, ...m.title }}>Messages</div>
          {!activeThread && !composing && (
            <button
              style={{ ...s.sendBtn, ...m.sendBtn, width: 'auto', padding: '8px 14px' }}
              onClick={() => { setComposing(true); setBody(''); setNewRecipientId(''); }}
            >
              + New Message
            </button>
          )}
        </div>
        <div style={{ ...s.sub, ...m.sub }}>
          {error
            ? <span style={{ color: '#B33A2E' }}>Couldn't load messages. Check your connection and try again.</span>
            : 'Message your cleaners and supervisors'}
        </div>
      </div>

      {composing ? (
        <div style={{ padding: 12 }}>
          <button style={{ ...s.linkBtn, ...m.linkBtn }} onClick={() => { setComposing(false); setBody(''); }}>← All messages</button>
          <div style={{ ...s.threadHead, ...m.threadHead }}>New Message</div>
          <div style={{ ...s.composeLabel, ...m.composeLabel }}>
            To:{' '}
            <select style={{ ...s.select, ...m.select }} value={newRecipientId} onChange={e => setNewRecipientId(e.target.value)}>
              <option value="">— Select recipient —</option>
              {workers.map(w => <option key={w.id} value={w.id}>{w.full_name} — {ROLE_LABELS[w.role] || w.role}</option>)}
            </select>
          </div>
          <textarea style={{ ...s.textarea, ...m.textarea }} rows={3} placeholder="Type a message..." value={body}
            maxLength={MESSAGE_MAX_LEN}
            onChange={e => setBody(e.target.value)} />
          <div style={{ textAlign: 'right', fontSize: 11, color: body.length > MESSAGE_MAX_LEN * 0.9 ? '#96600A' : '#5B6B72', marginTop: -4, marginBottom: 6 }}>
            {body.length}/{MESSAGE_MAX_LEN}
          </div>
          <button style={busy || !body.trim() || !newRecipientId ? { ...s.sendBtn, ...m.sendBtn, opacity: 0.6 } : { ...s.sendBtn, ...m.sendBtn }}
            disabled={busy || !body.trim() || !newRecipientId} onClick={handleStartNewMessage}>
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
      ) : activeThread ? (
        <div style={{ padding: 12 }}>
          <button style={{ ...s.linkBtn, ...m.linkBtn }} onClick={() => setActiveThread(null)}>← All messages</button>
          <div style={{ ...s.threadHead, ...m.threadHead }}>{nameOf(activeThread.otherId)}</div>
          <div style={s.thread}>
            {activeThread.messages.length === 0 && (
              <div style={{ ...s.empty, ...m.empty }}>No messages yet with {nameOf(activeThread.otherId)}. Send the first one below.</div>
            )}
            {activeThread.messages.slice().reverse().map(msg => {
              const mine = msg.sender_id === profile.id;
              return (
                <div key={msg.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                  <div style={{
                    maxWidth: '80%', padding: '8px 12px', borderRadius: 12,
                    background: mine ? '#2D5F7C' : '#FFFFFF',
                    border: mine ? 'none' : '1px solid #E4E0D8',
                    color: mine ? '#FFFFFF' : '#1A2B33', fontSize: 14, lineHeight: 1.4,
                  }}>
                    {msg.body}
                    <div style={{ fontSize: 10, color: mine ? 'rgba(255,255,255,0.75)' : '#5B6B72', marginTop: 4, textAlign: 'right' }}>{fmt(msg.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <textarea style={{ ...s.textarea, ...m.textarea }} rows={2} placeholder={`Reply to ${nameOf(activeThread.otherId)}...`}
            value={body} maxLength={MESSAGE_MAX_LEN} onChange={e => setBody(e.target.value)} />
          <div style={{ textAlign: 'right', fontSize: 11, color: body.length > MESSAGE_MAX_LEN * 0.9 ? '#96600A' : '#5B6B72', marginTop: -4, marginBottom: 6 }}>
            {body.length}/{MESSAGE_MAX_LEN}
          </div>
          <button style={busy || !body.trim() ? { ...s.sendBtn, ...m.sendBtn, opacity: 0.6 } : { ...s.sendBtn, ...m.sendBtn }}
            disabled={busy || !body.trim()} onClick={handleSend}>
            {busy ? 'Sending…' : 'Send Reply'}
          </button>
        </div>
      ) : (
        <div style={{ padding: 12 }}>
          {/* Only claim there's nothing here once we've actually looked —
              otherwise an unread message can be missed entirely. */}
          {loading && threads.length === 0 && <div style={{ ...s.empty, ...m.empty }}>Loading messages…</div>}
          {!loading && threads.length === 0 && (
            <div style={{ ...s.empty, ...m.empty }}>No messages yet. Tap "+ New Message" to start a conversation with a cleaner or supervisor.</div>
          )}
          {threads.map(t => (
            <button key={t.otherId} style={{ ...s.threadRow, ...m.threadRow }} onClick={() => openThread(t)}>
              <div style={{ ...s.threadRowName, ...m.threadRowName }}>
                {nameOf(t.otherId)}
                {t.unread > 0 && <span style={{ ...s.badge, ...m.badge }}>{t.unread}</span>}
              </div>
              <div style={{ ...s.threadRowPreview, ...m.threadRowPreview }}>
                {(t.messages[t.messages.length - 1]?.body || '').slice(0, 60)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
